/**
 * UpCart Accessibility Enhancer
 *
 * UpCart renders its side-cart drawer outside the theme (a portal appended to
 * the page) with no dialog semantics or focus management, so screen-reader and
 * keyboard users get a silently-appearing, non-trapped overlay. This module
 * enhances the third-party drawer in place:
 *   - marks the drawer panel as role="dialog" aria-modal="true" with a label
 *   - makes the rest of the page inert while the drawer is open, so browse-mode
 *     screen readers can't wander behind the "modal" (aria-modal alone doesn't
 *     constrain NVDA/JAWS, and is weaker still on a node in a shadow root)
 *   - hides the backdrop from assistive tech
 *   - moves focus into the drawer on open, traps Tab within it, supports Escape
 *     to close, and restores focus to the triggering element on close
 *   - promotes UpCart's clickable-but-unfocusable controls (e.g. the href-less
 *     checkout button) to tabbable, Enter/Space-operable role="button" controls,
 *     so keyboard users can actually reach and use them (WCAG 2.1.1)
 *   - re-applies all of the above when UpCart re-renders the drawer subtree
 *     (e.g. on a line-item quantity change), which otherwise strips it
 *   - stands down while a competing overlay is visible above the drawer (a
 *     marketing popup such as the Klaviyo add-to-cart offer or a Rebuy
 *     cross-sell modal): the page is un-inerted and the trap idles so the
 *     popup is fully usable, then isolation resumes when it closes. Inerting
 *     those popups froze their close buttons and hid them from assistive
 *     tech (TECH-12834). See syncPageIsolation.
 *
 * Satisfies WCAG 4.1.2 (Name, Role, Value) for the cart overlay.
 *
 * UpCart renders the drawer inside an open shadow root on #upCart, so the panel
 * and its contents are queried through that shadow root (not the light DOM), and
 * the active element is resolved by descending shadow roots.
 *
 * FOCUS SCOPE — why the trap spans the whole shadow root, not just #CartPopup:
 * UpCart re-renders the drawer aggressively (on open it paints a skeleton, then
 * populates upsells/subscribe/discount/checkout/package-protection a beat later,
 * and it re-renders again on any cart edit). Those re-renders REPLACE the
 * #CartPopup node and can move controls between #CartPopup and sibling containers
 * inside the shadow root. Anchoring the focus trap to #CartPopup therefore breaks:
 * the captured node goes stale and only the header/line-item controls stay
 * reachable, so checkout/upsells become keyboard-unreachable (WCAG 2.1.1). The
 * trap instead enumerates focusables from the STABLE #upCart shadow root
 * (getFocusScope), which UpCart never replaces, and DRIVES Tab itself (moving to
 * the next control in that list, never deferring to native Tab) so focus can't
 * fall through to whatever sits after the drawer in document order during a
 * mid-cycle re-render. Dialog semantics (role/aria-modal/label) still ride on the
 * visible #CartPopup panel and are re-applied when it is replaced.
 *
 * The panel interior is flat within the single #upCart shadow root (no nested
 * shadow roots), which getFocusScope's querySelectorAll relies on. If a future
 * UpCart update nests controls in their own shadow roots, the trap would find zero
 * focusables; trapFocus reports that case to Sentry so the regression is visible
 * rather than a silent keyboard trap.
 *
 * WATCHED THIRD-PARTY HOOKS — re-verify after any UpCart update:
 *   - body.upcartPopupShow ..... open/closed signal (class toggled on <body>)
 *   - #upCart (shadow host) .... open shadow root containing the drawer
 *   - #CartPopup ............... drawer panel   (fallback: .upcart-cart)
 *   - #UpcartPopupBackDrop ..... backdrop       (fallback: .upcart-backdrop)
 *   - .upcart-public-header__close-button  close control (fallback: [aria-label*="close" i])
 *     Escape closes by clicking this control; if the hook disappears we fall
 *     back to clearing the open class so Escape still dismisses (see closeViaUpCart).
 *   - .upcart-internal-addons (+ __content-title, [aria-label="Addon toggle"])
 *     addon cross-sell tile hooks used by labelAddonToggles.
 *
 * WATCHED OVERLAY HOOKS (other vendors, same re-verify rule): .klaviyo-form
 * marks an open Klaviyo popup (teasers and embeds don't render one at body
 * level); .rebuy-modal / .rebuy-widget[class*="modal"] mark Rebuy popups. The
 * generic visible [role="dialog"] / [aria-modal="true"] net backstops both and
 * covers apps we don't know about. See SELECTORS.COMPETING_OVERLAY.
 *
 * OBSERVED UPCART FOCUS BEHAVIOR (gm preview, TECH-12833) — the reason for the
 * focus guard (startFocusGuard): UpCart moves focus on its own, with no user
 * interaction: it autofocuses the first line-item link as the drawer populates,
 * and its quantity-input blur handler focuses a bare DIV, dumping focus to
 * <body>. Re-verify these after any UpCart update; if UpCart stops stealing
 * focus, the guard simply never fires.
 */

window.UFT = window.UFT || {};
window.UFT.UpCartAccessibility =
	window.UFT.UpCartAccessibility ||
	(function () {
		const OPEN_CLASS = 'upcartPopupShow';
		const LIVE_REGION_ID = 'CartStatusLiveRegion';
		// Not one of the theme's drawers, so it claims the hold itself; without it a corner card is
		// never told to step aside, and the card, a fixed [role="dialog"], reads as a competing
		// overlay this drawer would yield to. TTL as in drawer.js: handleClose always releases.
		const PAGE_INTERRUPT_NAME = 'upcart-drawer';
		const PAGE_INTERRUPT_HOLD_MS = 60 * 60 * 1000;

		const SELECTORS = {
			HOST: '#upCart',
			PANEL: '#CartPopup, .upcart-cart',
			BACKDROP: '#UpcartPopupBackDrop, .upcart-backdrop',
			CLOSE_BUTTON: '.upcart-public-header__close-button, [aria-label*="close" i]',
			// Stable cart toggle(s) in the theme header, used as a focus-restore
			// fallback when the element that opened the drawer was re-rendered away.
			CART_TOGGLE: 'a[href$="/cart"], [data-drawer-open="DrawerCart"], [aria-label="Cart" i]',
			// Addon cross-sell tile: UpCart labels its toggle generically ("Addon
			// toggle") with no product association; labelAddonToggles wires the
			// tile's product title onto it.
			ADDON: '.upcart-internal-addons',
			ADDON_TITLE: '.upcart-internal-addons__content-title',
			ADDON_TOGGLE: '[aria-label="Addon toggle"]',
			// Transient overlays that legitimately open ABOVE the drawer (marketing
			// popups fired by the same add-to-cart that opened it). While one is
			// visible the drawer is no longer the top layer and must not inert it or
			// fight it for focus (TECH-12834). See the header's overlay hooks note.
			COMPETING_OVERLAY:
				'.klaviyo-form, .rebuy-modal, .rebuy-widget[class*="modal"], [role="dialog"], [aria-modal="true"]',
			// Always-available utility widgets that stay operable while the drawer is
			// open: the theme's accessibility widget must keep reach (QA, TECH-12834)
			// exactly like the cart live region does.
			ISOLATION_EXEMPT: 'accessibility-toggle, [id$="drawer-accessibility"]',
			// tabindex="-1" is excluded on every entry (not just the bare
			// [tabindex] clause): the trap replaces native Tab, so it must honor
			// the native "programmatically focusable only" semantic — including
			// for controls we demote ourselves (see demoteTrademarkLinks).
			FOCUSABLE: [
				'a[href]:not([tabindex="-1"])',
				'button:not([disabled]):not([tabindex="-1"])',
				'input:not([disabled]):not([tabindex="-1"])',
				'select:not([disabled]):not([tabindex="-1"])',
				'textarea:not([disabled]):not([tabindex="-1"])',
				'[tabindex]:not([tabindex="-1"])',
			].join(', '),
		};

		const DIALOG_LABEL = 'Shopping cart';
		const FOCUS_DELAY_MS = 100;
		const KEYS = { TAB: 'Tab', ESCAPE: 'Escape', ENTER: 'Enter', SPACE: ' ' };

		// UpCart renders some action controls (notably the checkout button) as an <a>
		// with no href — clickable, but neither keyboard-focusable nor -operable. These
		// get promoted to tabbable role="button" controls (see promoteInteractiveControls)
		// so keyboard users can reach and fire them; without it checkout is unreachable
		// by keyboard (WCAG 2.1.1).
		const PROMOTABLE = 'a:not([href]):not([tabindex]), [role="button"]:not([tabindex])';

		// UpCart toggles body.upcartPopupShow before it renders the drawer panel,
		// so poll briefly for the panel after detecting the open state.
		const PANEL_POLL = { INTERVAL_MS: 50, MAX_ATTEMPTS: 20 };

		// The drawer opens empty and populates a beat later, so hold the initial
		// focus (which is what a screen reader reads as the dialog) until the
		// subtree stops mutating — otherwise it reads the empty dialog, then reads
		// again when the populate re-render moves focus (the double read-through QA
		// saw). QUIET_MS = settle window after the last mutation; MAX_MS caps the
		// wait so a drawer that never fully quiesces still gets focused once.
		const FOCUS_SETTLE = { QUIET_MS: 150, MAX_MS: 500 };

		// A drawer open is attributed to the control the user clicked only if that
		// click was this recent; older clicks fall back to the live active element.
		const TRIGGER_RECENT_MS = 1000;

		// When the control focus should return to is still in the DOM but
		// momentarily fails the visibility filter (UpCart hides the line-item row
		// briefly while it updates after a quantity-input blur), wait it out and
		// retry instead of re-seating onto a shifted list, which teleported the
		// shopper into the upsell carousel (QA TECH-12833 finding 3).
		const FOCUS_RESTORE_RETRY = { DELAY_MS: 150, MAX_ATTEMPTS: 3 };

		// A focus change is treated as shopper-driven only when it lands on the
		// control the shopper pointed at within this window of the pointerdown.
		// UpCart moves focus on its own (it focuses the first line-item link when
		// the drawer opens, focuses a bare DIV when the quantity input blurs, and
		// focuses the first upsell's variant select; QA TECH-12833), sometimes
		// within milliseconds of the shopper's own keystroke, so recency alone
		// cannot classify a move: keyboard focus moves are always trap-driven, and
		// only a click legitimately lands focus on an arbitrary control.
		const USER_INPUT_GRACE_MS = 400;

		// The guard only reverts unattributed in-drawer focus moves that ride on
		// drawer churn: both observed UpCart steals arrive during or shortly after
		// a mutation burst (the open populate; the post-blur row update, whose
		// steal lands about a second later). A focus move on a quiet DOM with no
		// input behind it is far more likely assistive-tech navigation — VoiceOver
		// and TalkBack swipes and NVDA/JAWS object navigation move DOM focus
		// without any page-visible input event — and must be accepted, not fought.
		const VENDOR_CHURN_MS = 1500;

		class UpCartAccessibility {
			constructor() {
				this.isOpen = false;
				this.panel = null;
				this.lastFocusedElement = null;
				this.inertedElements = [];
				this.panelObserver = null;
				this.bodyObserver = null;
				this.panelPollTimer = null;
				// True while a competing overlay (marketing popup) is visible above the
				// open drawer and the drawer has stood down (see syncPageIsolation).
				this.yieldedToOverlay = false;
				// Coalesces mutation bursts into one isolation sync per task.
				this.isolationSyncScheduled = false;
				// Deferred initial-focus state (see scheduleInitialFocus).
				this.hasFocusedOnOpen = false;
				this.focusSettleTimer = null;
				this.focusDelayTimer = null;
				this.focusDeadline = 0;
				// Trigger captured in the click's capture phase, before the drawer opens.
				this.pendingTrigger = null;
				this.pendingTriggerAt = 0;
				// Clickable-but-unfocusable controls we've made keyboard-operable.
				this.promotedControls = new WeakSet();
				// Index of the focused control in the trap's ordered list, updated only
				// on shopper-driven focus changes (see handleScopeFocusIn), so focus
				// can resume from, or be re-seated at, the right control after a
				// re-render or third-party focus steal knocks it out of the list.
				this.lastFocusIndex = -1;
				// The element focus is supposed to be on right now: whatever the trap
				// last landed on, the shopper last focused, or the panel itself. The
				// focus guard restores focus to this element (by identity, falling
				// back to an equivalent control, then to lastFocusIndex) when UpCart
				// moves focus on its own.
				this.expectedFocus = null;
				// Root the focus guard listeners are attached to: the shadow root,
				// which stays stable across UpCart re-renders (unlike the panel).
				this.focusGuardRoot = null;
				// True while one of our own programmatic focus moves is in flight
				// (trap steps, restores, panel focus), so the guard doesn't react to
				// itself.
				this.suppressFocusGuard = false;
				// The last pointerdown: where and when. A focusin matching this is
				// the shopper clicking a control; see isUserDrivenFocus.
				this.lastPointerDownAt = 0;
				this.lastPointerDownTarget = null;
				// Pending retry while the restore target is transiently invisible.
				this.focusRestoreTimer = null;
				// Single pending deferred escape check; focusout fires in bursts
				// during re-renders and one check covers them all.
				this.escapeCheckTimer = null;
				// When the drawer subtree last mutated; the guard only reverts
				// unattributed focus moves near churn (see VENDOR_CHURN_MS).
				this.lastScopeMutationAt = 0;
				// Whether the shopper has interacted at all since this open. UpCart
				// autofocuses a line-item link as the drawer populates; without this
				// flag that vendor focus was mistaken for the shopper's and cancelled
				// the initial dialog focus/announcement (QA TECH-12833 finding 1).
				this.userInteractedSinceOpen = false;
				this.boundHandleKeydown = this.handleKeydown.bind(this);
				this.boundCaptureTrigger = this.captureTrigger.bind(this);
				this.boundRecordUserInput = this.recordUserInput.bind(this);
				this.boundHandleScopeFocusIn = this.handleScopeFocusIn.bind(this);
				this.boundHandleScopeFocusOut = this.handleScopeFocusOut.bind(this);
				this.init();
			}

			init() {
				if (!document.body) {
					return;
				}

				// This drawer's focus announcement is the add feedback on this brand, so
				// tell the announcer to defer to it rather than double-announcing. Keyed to
				// this script: <cart-drawer> also registers, and through the dark-launch
				// overlap both are on the page, so an unkeyed registration let either one's
				// teardown cancel the other's.
				window.UFT.CartStatusAnnouncer?.registerCartDialog?.('upcart');

				// Record the control the user activates BEFORE UpCart opens the drawer and
				// moves focus. handleOpen runs from the async open observer, a beat after
				// the add + open, by which point the active element is no longer the real
				// trigger; capturing on click (capture phase, fires for mouse and keyboard
				// activation alike) gets it at the source for accurate focus restoration.
				document.addEventListener('click', this.boundCaptureTrigger, true);

				// Record real user input (capture phase, fires for shadow-DOM events
				// too). The focus guard uses this to tell the shopper's focus moves
				// from UpCart's own: UpCart focuses elements with no user interaction
				// at all (drawer-open autofocus, quantity-blur DIV focus), and those
				// must not update the trap position or suppress the dialog focus.
				document.addEventListener('pointerdown', this.boundRecordUserInput, true);
				document.addEventListener('keydown', this.boundRecordUserInput, true);

				this.observer = new MutationObserver(() => this.syncState());
				this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

				// Handle the drawer already being open when this runs.
				this.syncState();
			}

			recordUserInput(event) {
				if (event.type === 'pointerdown') {
					this.lastPointerDownAt = Date.now();
					this.lastPointerDownTarget = event.composedPath ? event.composedPath()[0] : event.target;
					// Record the pointed control as the shopper's position right here:
					// Safari and Firefox on macOS don't focus buttons on click, so no
					// focusin will ever confirm it, and without this the guard would
					// later restore a mouse shopper to a stale position.
					if (this.isOpen && this.hasFocusedOnOpen) {
						const scope = this.getFocusScope();
						const pointed = this.lastPointerDownTarget;
						const control = pointed && pointed.closest && pointed.closest(SELECTORS.FOCUSABLE);
						if (scope && control && scope.contains(control)) {
							this.expectedFocus = control;
							const index = this.getFocusableElements().indexOf(control);
							if (index !== -1) {
								this.lastFocusIndex = index;
							}
						}
					}
				}
				if (this.isOpen) {
					this.userInteractedSinceOpen = true;
				}
			}

			captureTrigger(event) {
				const path = event.composedPath ? event.composedPath() : [event.target];
				const host = document.querySelector(SELECTORS.HOST);
				// Ignore clicks that originated inside the drawer — those aren't the
				// control that opened it, and restoreFocus should return to the page.
				if (host && path.includes(host)) {
					return;
				}

				const target = path[0];
				// The click may land on an inner icon/span; resolve to the focusable
				// control. Store only a real focusable (never the bare target): a
				// non-focusable trigger would pass tryFocus's isConnected check, no-op on
				// .focus(), and short-circuit the restore fallback chain onto <body>.
				this.pendingTrigger = (target && target.closest && target.closest(SELECTORS.FOCUSABLE)) || null;
				this.pendingTriggerAt = Date.now();
			}

			syncState() {
				const open = document.body.classList.contains(OPEN_CLASS);
				if (open === this.isOpen) {
					return;
				}

				this.isOpen = open;
				if (open) {
					this.handleOpen();
				} else {
					this.handleClose();
				}
			}

			handleOpen() {
				// First: the claim notifies synchronously, so an open corner card is off the page before
				// syncPageIsolation asks what competes with this drawer.
				this.claimPageInterrupt();

				// The drawer is opening; its focus announcement will convey the add, so
				// stand the announcer's competing live-region message down (QA: it was
				// overshadowed by this focus move).
				window.UFT.CartStatusAnnouncer?.suppressPendingAnnouncement?.();

				// Each open re-arms the deferred initial focus and the trap's position.
				this.hasFocusedOnOpen = false;
				this.lastFocusIndex = -1;
				this.expectedFocus = null;
				this.userInteractedSinceOpen = false;
				// The open transition counts as churn: UpCart autofocuses a line-item
				// link around open even when the content was pre-rendered (an add via
				// fetch renders the drawer before the class ever toggles), so the
				// guard must not read that steal as a quiet-DOM assistive-tech move.
				this.lastScopeMutationAt = Date.now();
				// Resolve the element to return focus to on close (see resolveTrigger).
				this.lastFocusedElement = this.resolveTrigger();
				// Drop any poll chain still pending from a previous open so two chains
				// can't both reach enhancePanel after a fast close/reopen.
				this.stopPollingForPanel();
				this.waitForPanel(0);
			}

			resolveTrigger() {
				// Prefer the control the user just activated (captured before focus moved
				// into the drawer). Consume it here so a single click is attributed to at
				// most one open: a later non-click-driven open (async auto-open, or a
				// programmatic one) can't then be misattributed to a stale click. Fall back
				// to the live active element when there's no fresh, still-connected click.
				const trigger = this.pendingTrigger;
				const triggerAt = this.pendingTriggerAt;
				this.pendingTrigger = null;
				this.pendingTriggerAt = 0;

				if (trigger && trigger.isConnected && Date.now() - triggerAt <= TRIGGER_RECENT_MS) {
					return trigger;
				}
				return this.getActiveElement();
			}

			getCartRoot() {
				const host = document.querySelector(SELECTORS.HOST);
				return host?.shadowRoot || document;
			}

			waitForPanel(attempt) {
				this.panelPollTimer = null;

				// The drawer may have closed again before the panel rendered.
				if (!this.isOpen) {
					return;
				}

				const panel = this.getCartRoot().querySelector(SELECTORS.PANEL);
				if (panel) {
					this.panel = panel;
					this.enhancePanel();
					return;
				}

				if (attempt >= PANEL_POLL.MAX_ATTEMPTS) {
					// The drawer signalled open but never rendered a panel, so none of the
					// dialog semantics or focus management got applied. Report it. (Silent
					// when UpCart simply isn't on the page, since the class never toggles.)
					window.UFT.FailureReporter?.report?.(
						'upcart-accessibility-no-panel',
						'UpCartAccessibility: drawer opened but panel never rendered; dialog semantics and focus trap were not applied.'
					);
					return;
				}

				this.panelPollTimer = setTimeout(() => this.waitForPanel(attempt + 1), PANEL_POLL.INTERVAL_MS);
			}

			stopPollingForPanel() {
				if (this.panelPollTimer) {
					clearTimeout(this.panelPollTimer);
					this.panelPollTimer = null;
				}
			}

			enhancePanel() {
				this.applyDialogSemantics();
				this.enhanceControls();
				this.hideBackdrop();
				this.syncPageIsolation();
				this.observePanelSubtree();
				this.observeBodyMutations();
				this.startFocusGuard();
				// Cap how long we'll wait for the drawer to settle before focusing.
				this.focusDeadline = Date.now() + FOCUS_SETTLE.MAX_MS;
				this.scheduleInitialFocus();
				document.addEventListener('keydown', this.boundHandleKeydown);
			}

			handleClose() {
				this.releasePageInterrupt();
				document.removeEventListener('keydown', this.boundHandleKeydown);
				// Kill any in-flight panel poll / pending focus so none fire after a reopen.
				this.stopPollingForPanel();
				this.clearFocusSettleTimer();
				this.clearFocusDelayTimer();
				this.stopObservingPanelSubtree();
				this.stopObservingBodyMutations();
				this.stopFocusGuard();
				this.clearFocusRestoreTimer();
				this.clearEscapeCheckTimer();
				const overlayVisible = !!this.findCompetingOverlay();
				this.yieldedToOverlay = false;
				// Un-inert before restoring focus, or the focus target (trigger or cart
				// toggle) may sit inside a still-inert subtree and refuse focus.
				this.restorePage();
				// If a competing overlay is up as the drawer closes, it owns focus:
				// yanking focus back to the page trigger would rip the shopper out of it.
				if (overlayVisible) {
					this.lastFocusedElement = null;
				} else {
					this.restoreFocus();
				}
				this.panel = null;
				this.hasFocusedOnOpen = false;
			}

			// Optional in both directions, as in drawer.js: a page without the registry is
			// simply un-held.
			claimPageInterrupt() {
				window.UFT?.PageInterrupts?.claim(PAGE_INTERRUPT_NAME, { maxHoldMs: PAGE_INTERRUPT_HOLD_MS });
			}

			releasePageInterrupt() {
				window.UFT?.PageInterrupts?.release(PAGE_INTERRUPT_NAME);
			}

			// UpCart re-renders the drawer subtree on cart edits (e.g. a quantity
			// change), which replaces the panel node and strips the injected role/
			// aria-modal/tabindex. Watch the shadow subtree and re-enhance when that
			// happens; without this the keydown trap ends up bound to a detached node.
			observePanelSubtree() {
				// Drop any prior observer first: a stale poll chain that re-enhances
				// after a fast open/close/open would otherwise reassign panelObserver and
				// leak the previous one (stopObservingPanelSubtree only knows the latest).
				this.stopObservingPanelSubtree();

				const host = document.querySelector(SELECTORS.HOST);
				const root = host?.shadowRoot || host;
				if (!root) {
					// We're past enhancePanel, so a panel was found — but with no #upCart
					// host it must have come from the light-DOM .upcart-cart fallback, and
					// we have nothing to observe. Dialog semantics still applied; only
					// re-render recovery is off, so warn rather than error.
					window.UFT.FailureReporter?.report?.(
						'upcart-accessibility-no-host',
						'UpCartAccessibility: cart panel present but #upCart host/shadow root missing; re-render recovery is disabled.',
						{ level: 'warning' }
					);
					return;
				}

				this.panelObserver = new MutationObserver(() => this.handlePanelMutation());
				this.panelObserver.observe(root, { childList: true, subtree: true });
			}

			stopObservingPanelSubtree() {
				if (this.panelObserver) {
					this.panelObserver.disconnect();
					this.panelObserver = null;
				}
			}

			// Page isolation is only correct for the DOM as it stood when it ran, and
			// the light DOM keeps moving while the drawer is open: third parties inject
			// body-level nodes after the open (e.g. Skio's #skioReferralBanner, which
			// left a focusable element Tab could land on, and Klaviyo, which mounts its
			// popup root on add-to-cart), and overlays open and close by toggling
			// class/style/hidden on nodes already in the tree. Watch all of it and
			// re-sync. Shadow trees (the drawer's own churn) don't reach a light-DOM
			// observer, and the attribute filter excludes `inert`, so our own
			// isolatePage/restorePage writes can't feed back into this.
			observeBodyMutations() {
				this.stopObservingBodyMutations();
				this.bodyObserver = new MutationObserver(() => this.scheduleIsolationSync());
				this.bodyObserver.observe(document.body, {
					childList: true,
					subtree: true,
					attributes: true,
					attributeFilter: ['class', 'style', 'hidden'],
				});
			}

			stopObservingBodyMutations() {
				if (this.bodyObserver) {
					this.bodyObserver.disconnect();
					this.bodyObserver = null;
				}
			}

			// setTimeout, not requestAnimationFrame: rAF doesn't fire at all while the
			// page isn't rendering (hidden tab, occluded window), which would wedge
			// the one-shot flag and freeze isolation syncing until the next repaint.
			scheduleIsolationSync() {
				if (this.isolationSyncScheduled) {
					return;
				}
				this.isolationSyncScheduled = true;
				setTimeout(() => {
					this.isolationSyncScheduled = false;
					this.syncPageIsolation();
				}, 0);
			}

			handlePanelMutation() {
				this.lastScopeMutationAt = Date.now();
				if (!this.isOpen) {
					return;
				}

				const panel = this.getCartRoot().querySelector(SELECTORS.PANEL);
				if (!panel) {
					// Panel is momentarily gone mid-re-render; the next mutation will have it.
					return;
				}

				const semanticsIntact = panel === this.panel && panel.getAttribute('role') === 'dialog';
				if (!semanticsIntact) {
					// The node was replaced or its dialog semantics were stripped: re-point
					// and re-apply. (Skipped on plain in-panel text changes so we don't
					// thrash reapplying identical attributes on every price tick.)
					this.panel = panel;
					this.applyDialogSemantics();
					this.hideBackdrop();
					this.syncPageIsolation();
				}

				// Enhance on every mutation, not just on panel replacement: the checkout
				// button and other late controls render as the drawer populates while the
				// panel node (and its dialog role) stay intact. Cheap and idempotent — the
				// selectors match nothing once a control is already enhanced.
				this.enhanceControls();

				if (!this.hasFocusedOnOpen) {
					// Still settling toward the one initial focus — the re-render is UpCart
					// populating the drawer, so push the focus out until it quiesces rather
					// than focusing (and reading) partial content now.
					this.scheduleInitialFocus();
					return;
				}

				// Already announced once. Only pull focus back if a re-render knocked it
				// out of the drawer entirely; never re-focus the dialog just because it
				// re-rendered, which would make the screen reader read it all over again.
				this.recoverFocusIfLost();
			}

			applyDialogSemantics() {
				this.panel.setAttribute('role', 'dialog');
				this.panel.setAttribute('aria-modal', 'true');
				if (!this.panel.hasAttribute('aria-label')) {
					this.panel.setAttribute('aria-label', DIALOG_LABEL);
				}
				this.panel.setAttribute('tabindex', '-1');
			}

			// Idempotent per-render enhancement passes over UpCart's controls, run at
			// enhance time and again on every drawer mutation.
			enhanceControls() {
				this.promoteInteractiveControls();
				this.labelAddonToggles();
				this.demoteTrademarkLinks();
			}

			// UpCart labels its addon cross-sell toggle generically ("Addon toggle")
			// with no product association, so a screen-reader user lands on a
			// context-less switch (WCAG 4.1.2, 2.4.6; TECH-12833). Wire the tile's
			// product title onto the toggle and its checkbox ("Add <product>").
			// Idempotent: once relabelled, the vendor label no longer matches; a
			// re-render recreates it and gets relabelled again.
			labelAddonToggles() {
				const scope = this.getFocusScope();
				if (!scope) {
					return;
				}

				scope.querySelectorAll(SELECTORS.ADDON).forEach((addon) => {
					const toggle = addon.querySelector(SELECTORS.ADDON_TOGGLE);
					const title = addon.querySelector(SELECTORS.ADDON_TITLE);
					const name = title && title.textContent.trim();
					if (!toggle || !name) {
						return;
					}
					const label = `Add ${name}`;
					toggle.setAttribute('aria-label', label);
					// The checkbox inside is its own tab stop and had no name at all.
					const checkbox = toggle.querySelector('input');
					if (checkbox) {
						checkbox.setAttribute('aria-label', label);
					}
				});
			}

			// UpCart renders some product titles with the ™ split into its own
			// anchor: a meaningless extra tab stop that breaks the name apart for
			// screen readers (WCAG 1.3.1, 2.4.4; TECH-12833, "Cyclean™ AM"). Fold it
			// back in: out of the tab order (the FOCUSABLE selector honors the -1)
			// and hidden from AT; the symbol stays visible, and the title anchor
			// still reads naturally.
			demoteTrademarkLinks() {
				const scope = this.getFocusScope();
				if (!scope) {
					return;
				}

				scope.querySelectorAll('a').forEach((anchor) => {
					if (anchor.textContent.trim() === '™' && anchor.getAttribute('tabindex') !== '-1') {
						anchor.setAttribute('tabindex', '-1');
						anchor.setAttribute('aria-hidden', 'true');
					}
				});
			}

			// Make UpCart's clickable-but-unfocusable controls (the checkout <a> has no
			// href, so it's neither tabbable nor Enter/Space-operable) keyboard-usable:
			// give them tabindex="0" so the trap includes them, and role="button" so a
			// screen reader announces them correctly. Enter/Space activation is handled
			// in handleKeydown via the promotedControls set. Idempotent — once promoted,
			// a control gains a tabindex and no longer matches PROMOTABLE.
			promoteInteractiveControls() {
				const scope = this.getFocusScope();
				if (!scope) {
					return;
				}

				scope.querySelectorAll(PROMOTABLE).forEach((control) => {
					if (control.getClientRects().length === 0) {
						return;
					}
					control.setAttribute('tabindex', '0');
					if (control.tagName === 'A' && !control.hasAttribute('role')) {
						control.setAttribute('role', 'button');
					}
					this.promotedControls.add(control);
				});
			}

			hideBackdrop() {
				const backdrop = this.getCartRoot().querySelector(SELECTORS.BACKDROP);
				if (backdrop) {
					backdrop.setAttribute('aria-hidden', 'true');
				}
			}

			// aria-modal isn't enough for browse-mode screen readers, so make the rest
			// of the page genuinely unreachable with `inert` while the drawer is open.
			// Skip UpCart's own subtree, the cart live region (which must keep
			// announcing), and the always-operable utility widgets (ISOLATION_EXEMPT).
			// Idempotent, so it's safe to re-run on every page-DOM sync to catch any
			// body-level node added while the drawer was open. Callers outside the
			// suspend/resume logic go through syncPageIsolation, never call this
			// directly, so a competing overlay is never inerted.
			isolatePage() {
				const host = document.querySelector(SELECTORS.HOST);
				const liveRegion = document.getElementById(LIVE_REGION_ID);

				for (const child of document.body.children) {
					if (child.inert) {
						continue;
					}
					if (host && (child === host || child.contains(host))) {
						continue;
					}
					if (liveRegion && (child === liveRegion || child.contains(liveRegion))) {
						continue;
					}
					if (child.matches(SELECTORS.ISOLATION_EXEMPT)) {
						continue;
					}

					child.inert = true;
					this.inertedElements.push(child);
				}
			}

			restorePage() {
				this.inertedElements.forEach((element) => {
					element.inert = false;
				});
				this.inertedElements = [];
			}

			// The drawer is modal against the page, not against other overlays. The
			// same add-to-cart that opens it also fires marketing popups (Klaviyo's
			// mystery-discount offer, Rebuy cross-sell modals) that open above it, at
			// body level; inerting those froze their close buttons and dropped them
			// from the accessibility tree (TECH-12834). So: while a competing overlay
			// is visible, stand down entirely (un-inert the page, idle the trap and
			// every focus pull) and let the overlay own the interaction; when it goes
			// away, re-isolate and put the shopper back in the drawer. All isolation
			// paths funnel through here so the decision is made in exactly one place.
			syncPageIsolation() {
				if (!this.isOpen) {
					return;
				}

				const overlay = this.findCompetingOverlay();
				if (overlay) {
					if (!this.yieldedToOverlay) {
						this.yieldedToOverlay = true;
						// Kill any pending focus pull so the drawer can't steal focus from
						// the overlay, then free the whole page: the overlay needs its own
						// backdrop and controls live, and a partial release would leave
						// whichever body child hosts it dead anyway.
						this.clearFocusSettleTimer();
						this.clearFocusDelayTimer();
						this.clearFocusRestoreTimer();
						this.restorePage();
					}
					return;
				}

				if (this.yieldedToOverlay) {
					this.yieldedToOverlay = false;
					this.isolatePage();
					// The overlay took focus with it when it closed; put the shopper back
					// in the drawer they still have open (announces it if it never got
					// its initial read).
					this.recoverFocusIfLost();
					return;
				}

				this.isolatePage();
			}

			findCompetingOverlay() {
				const host = document.querySelector(SELECTORS.HOST);
				const liveRegion = document.getElementById(LIVE_REGION_ID);

				for (const child of document.body.children) {
					if (host && (child === host || child.contains(host))) {
						continue;
					}
					if (liveRegion && (child === liveRegion || child.contains(liveRegion))) {
						continue;
					}
					if (typeof child.matches !== 'function') {
						continue;
					}

					const candidates = child.matches(SELECTORS.COMPETING_OVERLAY)
						? [child]
						: [...child.querySelectorAll(SELECTORS.COMPETING_OVERLAY)];
					if (candidates.some((element) => this.isOverlayVisible(element) && this.isInFixedLayer(element, child))) {
						return child;
					}
				}
				return null;
			}

			// Overlays render in a fixed-position layer (Klaviyo's popup wrapper,
			// Rebuy's modal, the UA top layer for dialog:modal all do); in-page
			// content that merely matches an overlay hook, like a Klaviyo newsletter
			// EMBED scrolled into view in the footer, does not. Requiring a fixed
			// ancestor keeps static content from being mistaken for the active layer,
			// which would permanently disable isolation on pages that contain it.
			isInFixedLayer(element, root) {
				for (let node = element; node; node = node.parentElement) {
					if (window.getComputedStyle(node).position === 'fixed') {
						return true;
					}
					if (node === root) {
						break;
					}
				}
				return false;
			}

			// Visible enough to be the active layer: has a box that intersects the
			// viewport and isn't visibility-hidden. The viewport check matters because
			// several apps keep sized dialog nodes parked offscreen while "closed"
			// (e.g. Sezzle's modal); treating those as open would permanently disable
			// isolation and silently regress the original TECH-12728 fix.
			isOverlayVisible(element) {
				const rect = element.getBoundingClientRect();
				if (rect.width === 0 || rect.height === 0) {
					return false;
				}
				const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
				const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
				if (rect.bottom <= 0 || rect.right <= 0 || rect.top >= viewportHeight || rect.left >= viewportWidth) {
					return false;
				}
				if (typeof element.checkVisibility === 'function') {
					return element.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true });
				}
				return true;
			}

			// Defer the initial focus until the drawer stops re-rendering: reset a short
			// quiet-period timer on each mutation, but never past a hard deadline, so a
			// chatty drawer still gets focused once. This is what makes the dialog get
			// read a single time (populated), instead of once empty then again on populate.
			scheduleInitialFocus() {
				if (this.hasFocusedOnOpen || this.yieldedToOverlay) {
					return;
				}
				this.clearFocusSettleTimer();
				const remaining = this.focusDeadline - Date.now();
				const delay = Math.max(0, Math.min(FOCUS_SETTLE.QUIET_MS, remaining));
				this.focusSettleTimer = setTimeout(() => {
					this.focusSettleTimer = null;
					this.focusPanelOnce();
				}, delay);
			}

			clearFocusSettleTimer() {
				if (this.focusSettleTimer) {
					clearTimeout(this.focusSettleTimer);
					this.focusSettleTimer = null;
				}
			}

			focusPanelOnce() {
				// While yielded to a competing overlay, don't consume the one-shot:
				// the resume path re-enters here so the dialog still gets its read.
				if (this.hasFocusedOnOpen || this.yieldedToOverlay) {
					return;
				}
				this.hasFocusedOnOpen = true;
				// If the shopper THEMSELVES moved focus into the drawer (e.g. clicked
				// the discount field while it settled), don't yank it away to announce
				// the dialog — their own focus is a better "settled" signal than the
				// timer, and stealing it mid-keystroke drops input. The dialog
				// announcement is what handleOpen suppressed the live-region add
				// message in favor of, and it now isn't happening, so hand the add
				// feedback back to the announcer; otherwise the shopper gets no
				// confirmation at all. Gated on real interaction because UpCart
				// autofocuses a line-item link on open: vendor focus must not cancel
				// the dialog focus (QA TECH-12833 finding 1).
				if (this.userHasFocusInDrawer() && this.userInteractedSinceOpen) {
					window.UFT.CartStatusAnnouncer?.releasePendingAnnouncement?.();
					return;
				}
				// Re-arm the announcer suppression at the moment the dialog actually takes
				// focus (the real add feedback), covering a cart:item-added that landed late
				// because the drawer took a while to populate.
				window.UFT.CartStatusAnnouncer?.suppressPendingAnnouncement?.();
				this.focusPanel();
			}

			focusPanel() {
				// Focus the panel itself (role="dialog" + aria-label) so screen readers
				// announce "Shopping cart, dialog", rather than a bare "Close".
				const target = this.panel;
				this.clearFocusDelayTimer();
				this.focusDelayTimer = setTimeout(() => {
					this.focusDelayTimer = null;
					// Bail if the drawer closed, the panel was re-rendered away, or a
					// competing overlay took the top layer during the delay.
					if (!this.isOpen || this.yieldedToOverlay || !target?.isConnected) {
						return;
					}
					// The shopper moved focus into the drawer during the delay: leave it
					// alone (grabbing it here is what re-announced on a close/reopen
					// inside the window) and, as in focusPanelOnce, hand the suppressed
					// add feedback back to the live-region announcer. Same interaction
					// gate as there: UpCart's own autofocus doesn't count.
					if (this.userHasFocusInDrawer() && this.userInteractedSinceOpen) {
						window.UFT.CartStatusAnnouncer?.releasePendingAnnouncement?.();
						return;
					}
					this.suppressFocusGuard = true;
					target.focus();
					this.suppressFocusGuard = false;
					this.expectedFocus = target;
				}, FOCUS_DELAY_MS);
			}

			clearFocusDelayTimer() {
				if (this.focusDelayTimer) {
					clearTimeout(this.focusDelayTimer);
					this.focusDelayTimer = null;
				}
			}

			// True when the real focused element (shadow-aware) is already inside the
			// drawer — i.e. the shopper is interacting and we shouldn't move their focus.
			userHasFocusInDrawer() {
				const scope = this.getFocusScope();
				return !!(scope && scope.contains(this.getActiveElement()));
			}

			// FOCUS GUARD — while the drawer is open and settled, focus only moves by
			// shopper intent or our own management. UpCart moves focus on its own
			// (verified on the gm preview, QA TECH-12833): it autofocuses the first
			// line-item link as the drawer populates, and on quantity-input blur its
			// bundle focuses a bare DIV, dumping focus to <body>. Untreated, those
			// vendor moves polluted the trap position (first Shift+Tab landed on the
			// close button), stranded focus on the page mid-Tab-cycle, and teleported
			// shoppers into the upsell carousel. The guard classifies every focus
			// change and reverts the ones the shopper didn't cause.
			startFocusGuard() {
				this.stopFocusGuard();
				const scope = this.getFocusScope();
				if (!scope) {
					return;
				}
				// In the light-DOM fallback (no shadow host) this is the panel, so the
				// guard dies with the panel on a re-render, consistent with the rest
				// of that degraded path (see observePanelSubtree).
				this.focusGuardRoot = scope;
				scope.addEventListener('focusin', this.boundHandleScopeFocusIn);
				scope.addEventListener('focusout', this.boundHandleScopeFocusOut);
			}

			stopFocusGuard() {
				if (this.focusGuardRoot) {
					this.focusGuardRoot.removeEventListener('focusin', this.boundHandleScopeFocusIn);
					this.focusGuardRoot.removeEventListener('focusout', this.boundHandleScopeFocusOut);
					this.focusGuardRoot = null;
				}
			}

			handleScopeFocusIn(event) {
				if (!this.isOpen || this.suppressFocusGuard || !this.hasFocusedOnOpen) {
					// Pre-settle focus churn is UpCart populating; scheduleInitialFocus
					// owns focus until the drawer settles, and focusPanelOnce overrides
					// any vendor autofocus then (the shopper hasn't interacted yet).
					return;
				}

				// The listener sits inside the shadow root, so event.target is the
				// real inner control, not the retargeted host.
				const target = event.target;
				if (target === this.panel || target === this.expectedFocus) {
					this.expectedFocus = target;
					return;
				}

				// Shopper-driven move (a click or tap on a control, including a
				// screen reader's synthesized activation): track it as the new
				// resume position. Trap-driven Tab moves never reach here (the trap
				// suppresses the guard and records expectedFocus itself).
				if (this.isUserDrivenFocus(target)) {
					this.expectedFocus = target;
					this.lastFocusIndex = this.getFocusableElements().indexOf(target);
					return;
				}

				// Unattributed move on a quiet DOM: almost certainly assistive-tech
				// navigation (VoiceOver/TalkBack swipe, NVDA/JAWS object nav), which
				// moves DOM focus without any input event this page can see. Accept
				// it; UpCart's steals ride on drawer churn and are handled below.
				if (Date.now() - this.lastScopeMutationAt > VENDOR_CHURN_MS) {
					this.expectedFocus = target;
					const index = this.getFocusableElements().indexOf(target);
					if (index !== -1) {
						this.lastFocusIndex = index;
					}
					return;
				}

				// UpCart moved focus during churn with no user interaction behind
				// it: put focus back where the shopper left it.
				this.restoreExpectedFocus();
			}

			// UpCart's quantity-blur handler focuses a bare DIV, which lands focus on
			// <body> without any focusin inside the scope, so catch escapes on
			// focusout too. Deferred a tick because focusout fires before the new
			// focus target is committed, and coalesced: re-renders fire focusout in
			// bursts and one check covers them all.
			handleScopeFocusOut() {
				if (!this.isOpen || this.escapeCheckTimer) {
					return;
				}
				this.escapeCheckTimer = setTimeout(() => {
					this.escapeCheckTimer = null;
					if (!this.isOpen || !this.hasFocusedOnOpen || this.suppressFocusGuard) {
						return;
					}
					// Focus moving into a competing overlay is the one legitimate exit.
					// Converge the yield state before judging: the overlay may have
					// mounted and grabbed focus in this same tick, ahead of the
					// isolation sync timer (TECH-12834).
					this.syncPageIsolation();
					if (this.yieldedToOverlay) {
						return;
					}
					const scope = this.getFocusScope();
					if (scope && !scope.contains(this.getActiveElement())) {
						// Focus left the open drawer with no overlay to justify it. That
						// is never legitimate (the page behind is inert, and closing
						// flips isOpen first), so pull it back however it happened.
						this.restoreExpectedFocus();
					}
				}, 0);
			}

			clearEscapeCheckTimer() {
				if (this.escapeCheckTimer) {
					clearTimeout(this.escapeCheckTimer);
					this.escapeCheckTimer = null;
				}
			}

			// A focus move counts as the shopper's only when it lands on the control
			// they just pointed at. Keyboard moves never need this path: the trap
			// drives them itself (with the guard suppressed) and records the landing
			// in expectedFocus. Recency alone can't classify: UpCart steals focus
			// within milliseconds of a Tab, so a keydown-based grace window would
			// bless the steal (it did, in testing: the upsell-select steal landed
			// inside the window and got tracked as the shopper's position).
			isUserDrivenFocus(target) {
				// Input from before the open (the click that opened the drawer)
				// doesn't count; only interaction inside the open drawer qualifies.
				if (!this.userInteractedSinceOpen) {
					return false;
				}
				const pointed = this.lastPointerDownTarget;
				if (!pointed || Date.now() - this.lastPointerDownAt > USER_INPUT_GRACE_MS) {
					return false;
				}
				// The pointerdown may land on an inner icon/span of the control that
				// then takes focus, or directly on the control itself.
				return (
					target === pointed ||
					(target.contains && target.contains(pointed)) ||
					(pointed.contains && pointed.contains(target))
				);
			}

			// Put focus back on the element it is supposed to be on. Prefer the exact
			// element (it usually survives: UpCart's steals move focus without
			// re-rendering), then an equivalent control (same tag + label, for the
			// re-render case where the node was replaced), then the tracked list
			// position, and only then the panel, which re-announces the whole dialog
			// and is the last resort precisely because it loses the shopper's place.
			restoreExpectedFocus(attempt = 0) {
				// Never wrestle focus from a competing overlay (defense in depth: the
				// guard's callers are individually gated too).
				if (this.yieldedToOverlay) {
					return;
				}
				if (this.focusRestoreTimer) {
					// A retry is already scheduled; it will finish the job. This also
					// keeps steal-reverts that fire during the wait from re-entering.
					return;
				}

				const focusable = this.getFocusableElements();
				if (this.expectedFocus && this.expectedFocus.isConnected) {
					if (focusable.includes(this.expectedFocus)) {
						if (this.tryGuardedFocus(this.expectedFocus, focusable)) {
							return;
						}
					} else if (attempt < FOCUS_RESTORE_RETRY.MAX_ATTEMPTS) {
						// Still in the DOM but currently failing the visibility filter:
						// UpCart hides the line-item row briefly while it updates, and
						// the browser would refuse the focus. Wait the transient out
						// rather than re-seating onto a shifted list right now.
						this.focusRestoreTimer = setTimeout(() => {
							this.focusRestoreTimer = null;
							if (this.isOpen) {
								this.restoreExpectedFocus(attempt + 1);
							}
						}, FOCUS_RESTORE_RETRY.DELAY_MS);
						return;
					}
				}

				if (this.expectedFocus) {
					const equivalent = this.findEquivalentControl(this.expectedFocus, focusable);
					if (equivalent && this.tryGuardedFocus(equivalent, focusable)) {
						return;
					}
				}
				if (this.lastFocusIndex >= 0 && focusable.length > 0) {
					const byIndex = focusable[Math.min(this.lastFocusIndex, focusable.length - 1)];
					if (this.tryGuardedFocus(byIndex, focusable)) {
						return;
					}
				}

				// Nothing to restore to (or nothing took focus): fall back to the
				// panel, which re-announces the dialog the shopper is in.
				this.hasFocusedOnOpen = false;
				this.focusPanelOnce();
			}

			clearFocusRestoreTimer() {
				if (this.focusRestoreTimer) {
					clearTimeout(this.focusRestoreTimer);
					this.focusRestoreTimer = null;
				}
			}

			tryGuardedFocus(target, focusable) {
				this.suppressFocusGuard = true;
				target.focus();
				this.suppressFocusGuard = false;
				if (this.getActiveElement() === target) {
					this.expectedFocus = target;
					this.lastFocusIndex = focusable.indexOf(target);
					return true;
				}
				return false;
			}

			// After a re-render replaced the focused node, find its replacement: the
			// control with the same tag and accessible label, preferring the one
			// nearest the old list position when labels repeat across line items
			// (e.g. every item has an "Increase quantity" button).
			findEquivalentControl(previous, focusable) {
				const label = previous.getAttribute && previous.getAttribute('aria-label');
				if (!label) {
					return null;
				}
				const matches = focusable.filter(
					(el) => el.tagName === previous.tagName && el.getAttribute('aria-label') === label
				);
				if (matches.length === 0) {
					return null;
				}
				return matches.reduce((best, el) =>
					Math.abs(focusable.indexOf(el) - this.lastFocusIndex) <
					Math.abs(focusable.indexOf(best) - this.lastFocusIndex)
						? el
						: best
				);
			}

			recoverFocusIfLost() {
				// A competing overlay owns focus while it is up; pulling focus back into
				// the drawer from under it is exactly the fight this yields out of.
				if (this.yieldedToOverlay) {
					return;
				}
				// A re-render can detach the focused node, dropping focus to <body>.
				// Only intervene if focus left the whole drawer (the shadow root); if
				// it's still on any drawer control, leave it be so we don't
				// re-announce.
				const scope = this.getFocusScope();
				if (!scope || scope.contains(this.getActiveElement())) {
					return;
				}
				this.restoreExpectedFocus();
			}

			restoreFocus() {
				const trigger = this.lastFocusedElement;
				this.lastFocusedElement = null;

				if (this.tryFocus(trigger)) {
					return;
				}

				// The trigger was re-rendered/detached while the drawer was open. Fall
				// back to a stable cart toggle, then to the main landmark, so focus never
				// lands nowhere.
				if (this.tryFocus(this.getCartToggle())) {
					return;
				}

				this.focusFallbackLandmark();
			}

			focusFallbackLandmark() {
				// Prefer the #main landmark (the skip-nav target) over bare <body>, which
				// gives a screen-reader user no sense of place. Remove tabindex right
				// after focusing — focus survives the removal — so we don't leave a
				// permanent tabindex on a shared element.
				const landmark = document.getElementById('main') || document.body;
				landmark.setAttribute('tabindex', '-1');
				landmark.focus();
				landmark.removeAttribute('tabindex');
			}

			tryFocus(element) {
				if (element && typeof element.focus === 'function' && element.isConnected) {
					element.focus();
					// Confirm focus actually landed: .focus() is a silent no-op on an
					// unfocusable element, and returning true regardless would short-circuit
					// the cart-toggle/#main fallback chain and strand focus on <body>.
					return this.getActiveElement() === element;
				}
				return false;
			}

			getCartToggle() {
				const toggles = [...document.querySelectorAll(SELECTORS.CART_TOGGLE)];
				return toggles.find((element) => element.getClientRects().length > 0) || toggles[0] || null;
			}

			handleKeydown(event) {
				// While a competing overlay is up it owns the keyboard: its Escape must
				// close it (not the drawer underneath), and Tab must walk its controls.
				if (!this.panel || this.yieldedToOverlay) {
					return;
				}

				if (event.key === KEYS.ESCAPE) {
					this.closeViaUpCart();
					return;
				}

				if (event.key === KEYS.TAB) {
					this.trapFocus(event);
					return;
				}

				if (event.key === KEYS.ENTER || event.key === KEYS.SPACE) {
					this.activatePromotedControl(event);
				}
			}

			activatePromotedControl(event) {
				// A control we promoted (e.g. the href-less checkout <a>) has no native
				// Enter/Space activation, so drive its click ourselves. Scoped to promoted
				// controls only, so real inputs/selects keep their native key behaviour.
				const active = this.getActiveElement();
				if (this.promotedControls.has(active)) {
					event.preventDefault();
					active.click();
				}
			}

			closeViaUpCart() {
				// Close through UpCart's own control so the vendor tears the drawer down
				// its normal way (animation, internal state, body class). If that hook
				// ever disappears, clear the open class so Escape still dismisses for
				// keyboard users and our own teardown (handleClose) still runs.
				// Query the whole shadow root, not the (possibly re-rendered) panel node.
				const scope = this.getFocusScope();
				const closeButton = scope?.querySelector(SELECTORS.CLOSE_BUTTON);
				if (closeButton) {
					closeButton.click();
					return;
				}

				// Both close-button selectors drifted. Escape still dismisses via the
				// class-clear below, but the vendor hook changed — flag it (degraded).
				window.UFT.FailureReporter?.report?.(
					'upcart-accessibility-close-button-missing',
					'UpCartAccessibility: UpCart close-button selectors no longer match; Escape fell back to clearing the open class.',
					{ level: 'warning' }
				);
				document.body.classList.remove(OPEN_CLASS);
			}

			trapFocus(event) {
				const focusable = this.getFocusableElements();
				if (focusable.length === 0) {
					event.preventDefault();
					this.panel.focus();
					// Zero focusables in an open cart is anomalous — its controls
					// (steppers, checkout, close) should be reachable. The likely cause is
					// UpCart nesting them in their own shadow roots, which the flat
					// querySelectorAll in getFocusableElements can't cross (see header).
					// That parks focus on the panel: a real keyboard trap, so surface it.
					window.UFT.FailureReporter?.report?.(
						'upcart-accessibility-focus-trap-empty',
						'UpCartAccessibility: open cart drawer has zero focusable elements; focus parked on the panel (UpCart may have nested controls in shadow roots).'
					);
					return;
				}

				// Fully manage Tab rather than intercepting only at the ends: always
				// preventDefault and move focus to the next control in OUR ordered list.
				// Driving focus ourselves keeps every step inside the drawer.
				event.preventDefault();

				const active = this.getActiveElement();
				const step = event.shiftKey ? -1 : 1;
				const index = focusable.indexOf(active);

				let next;
				if (index === -1) {
					// Focus isn't on a known control: either the dialog container, or a
					// re-render / third-party auto-focus knocked it out of the list.
					// Third parties (e.g. Skio's #skioReferralBanner) re-render body-level
					// nodes non-inert for an instant and steal focus; if that node is
					// outside the drawer, re-inert the page so it can't grab focus again.
					// Then continue from where we last were, in the Tab direction, rather
					// than snapping back to the first control — snapping back stranded the
					// keyboard user at the top of the cart, never reaching checkout.
					const scope = this.getFocusScope();
					if (scope && !scope.contains(active)) {
						this.syncPageIsolation();
					}
					// On a fresh open nothing has been focused yet (lastFocusIndex is -1),
					// so enter the list at its edge: first control for Tab, last for
					// Shift+Tab. The resume math below would land the first Shift+Tab on
					// the second-to-last control, skipping checkout.
					if (this.lastFocusIndex === -1) {
						next = step === 1 ? 0 : focusable.length - 1;
					} else {
						next = (this.lastFocusIndex + step + focusable.length) % focusable.length;
					}
				} else {
					next = (index + step + focusable.length) % focusable.length;
				}

				// Move focus, skipping any candidate the browser silently refuses to
				// focus (a stray unfocusable node that slipped past the visibility
				// filter). Bounded to one wrap so a drawer of entirely-unfocusable
				// controls can't spin forever — and if none take focus we leave it put
				// rather than trap. Guard suppressed: these are our own moves.
				this.suppressFocusGuard = true;
				for (let attempts = focusable.length; attempts > 0; attempts--) {
					const before = this.getActiveElement();
					focusable[next].focus();
					const after = this.getActiveElement();
					if (after === focusable[next]) {
						break;
					}
					if (after !== before) {
						// Focus moved, just not to our candidate: a vendor handler stole
						// it in the same task (UpCart's quantity-blur handler focuses a
						// bare DIV when Tab leaves the quantity input, which skipped the
						// + button; QA TECH-12833 finding 2). The candidate is genuinely
						// focusable, so re-assert it. If the vendor steals again inside
						// this same (guard-suppressed) task, no focusin will ever alert
						// the guard, so hand it the fight once this task unwinds. A true
						// refusal leaves the active element unchanged, which is the only
						// case worth skipping.
						focusable[next].focus();
						if (this.getActiveElement() !== focusable[next]) {
							setTimeout(() => {
								if (this.isOpen) {
									this.restoreExpectedFocus();
								}
							}, 0);
						}
						break;
					}
					next = (next + step + focusable.length) % focusable.length;
				}
				this.suppressFocusGuard = false;

				this.lastFocusIndex = next;
				// The trap owns this move: record where focus is supposed to sit so
				// the focus guard can put it back if UpCart yanks it afterwards.
				this.expectedFocus = focusable[next];
			}

			// Focus lives inside UpCart's shadow root, so document.activeElement only
			// reports the host. Descend shadow roots to find the real focused element.
			getActiveElement() {
				let element = document.activeElement;
				while (element?.shadowRoot?.activeElement) {
					element = element.shadowRoot.activeElement;
				}
				return element;
			}

			// The stable container the focus trap operates over: the #upCart shadow root
			// (which UpCart never replaces), so enumeration and the escape check survive
			// the vendor re-rendering and relocating #CartPopup's contents. Falls back to
			// the panel itself only when there is no shadow host (the light-DOM path), so
			// the trap is never scoped to the whole document.
			getFocusScope() {
				const host = document.querySelector(SELECTORS.HOST);
				return host?.shadowRoot || this.panel;
			}

			getFocusableElements() {
				const scope = this.getFocusScope();
				if (!scope) {
					return [];
				}

				return [...scope.querySelectorAll(SELECTORS.FOCUSABLE)].filter((element) => this.isFocusableVisible(element));
			}

			isFocusableVisible(element) {
				// getClientRects() alone keeps visibility:hidden / content-visibility
				// elements (they still have layout boxes), but the browser refuses to
				// focus them — and since the trap fully drives Tab, a hidden node in the
				// list would stall it. checkVisibility() (widely supported) filters those.
				if (element.getClientRects().length === 0) {
					return false;
				}
				if (typeof element.checkVisibility === 'function') {
					return element.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true });
				}
				return true;
			}
		}

		return new UpCartAccessibility();
	})();
